import { useState } from "react";
import { AlertTriangle, CheckCircle2, Eye, QrCode, Sparkles, User, XCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { fetchWpwReturnItemPreview } from "../../api";
import { useWpwReturnBatch, useWpwReturnMutations } from "../../hooks";
import type { WpwMassRow, WpwReturnItem } from "../../types";

// Forma tytułu prawnego wykryta przez AI z treści podpisanego zwrotu (odpłatne/nieodpłatne).
const Z7_FORM_LABEL: Record<string, string> = {
  oswiadczenie_nieodplatne: "ośw. woli nieodpłatne",
  oswiadczenie_odplatne: "ośw. woli odpłatne",
};

/**
 * Przegląd pozycji zwrotu z terenu (wzorzec przeglądu ekstrakcji wypisów):
 * każda pozycja = zakres stron zbiorczego skanu dopasowany do działki (QR / AI /
 * ręcznie). Pozycja z wykrytymi dopiskami/warunkami ma zablokowany akcept do
 * czasu wpisania notatki z przeglądu — warunków w 99% nie akceptujemy.
 * „Zastosuj zaakceptowane” tnie PDF per działka i podpina zwrot do wniosku
 * pakietu ORAZ przekazań WPW właścicieli (odrzucenie → problem u negocjatora).
 */
export function FieldReturnReviewModal({
  batchId, rows, canMutate, onClose,
}: {
  batchId: string;
  rows: WpwMassRow[];
  canMutate: boolean;
  onClose: () => void;
}) {
  const { data: batch, isLoading } = useWpwReturnBatch(batchId);
  const rm = useWpwReturnMutations();
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const [previewing, setPreviewing] = useState<string | null>(null);

  const items = batch?.items ?? [];
  const accepted = items.filter((i) => i.status === "accepted").length;
  const pending = items.filter((i) => i.status === "proposed").length;

  const openPreview = async (item: WpwReturnItem) => {
    setPreviewing(item.id);
    try {
      const blob = await fetchWpwReturnItemPreview(item.id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setError("Nie udało się pobrać podglądu wycinka.");
    } finally {
      setPreviewing(null);
    }
  };

  const decide = (item: WpwReturnItem, decision: "accept" | "reject") => {
    setError("");
    rm.patchItem.mutate(
      { itemId: item.id, decision, note: notes[item.id] ?? "" },
      { onError: (e) => setError(extractError(e)) },
    );
  };

  const reassign = (item: WpwReturnItem, parcelId: string) => {
    setError("");
    if (!parcelId) return;
    rm.patchItem.mutate(
      { itemId: item.id, parcel_id: parcelId },
      { onError: (e) => setError(extractError(e)) },
    );
  };

  const matchedIcon = (item: WpwReturnItem) =>
    item.matched_by === "qr" ? (
      <QrCode size={12} className="text-green-700" aria-label="dopasowanie po QR" />
    ) : item.matched_by === "ai" ? (
      <Sparkles size={12} className="text-blue-600" aria-label="dopasowanie AI" />
    ) : (
      <User size={12} className="text-[var(--color-text-muted)]" aria-label="dopasowanie ręczne" />
    );

  return (
    <Modal open onClose={onClose} title="Zwrot z terenu — przegląd pozycji" size="xl">
      <div className="space-y-3">
        {isLoading && <div className="py-8 flex justify-center"><Spinner /></div>}
        {batch && (
          <>
            <p className="text-[11px] text-[var(--color-text-muted)]">
              {batch.file?.original_filename} · {batch.page_count ?? "?"} str. · pozycji: {items.length}
              {batch.unmatched_pages.length > 0 && (
                <> · strony bez przypisania: {batch.unmatched_pages.join(", ")}</>
              )}
              {batch.ai_cost_usd && <> · koszt AI: {batch.ai_cost_usd} USD</>}
            </p>

            {error && (
              <p className="text-xs text-red-600 flex items-center gap-1"><XCircle size={13} /> {error}</p>
            )}

            <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
              {items.map((item) => (
                <div
                  key={item.id}
                  className={`border px-2 py-2 space-y-1.5 ${
                    item.has_annotations && item.status === "proposed"
                      ? "border-amber-400 bg-amber-50/50 dark:border-amber-500/50 dark:bg-amber-500/10"
                      : "border-[var(--color-border-muted)]"
                  }`}
                >
                  <div className="flex flex-wrap items-center gap-2 text-[11px]">
                    {matchedIcon(item)}
                    <span className="font-mono">str. {item.pages}</span>
                    {item.application ? (
                      <span className="font-mono">{item.application.number} · dz. {item.teryt}</span>
                    ) : item.parcel_id ? (
                      <span className="font-mono">dz. {item.teryt} (bez wniosku!)</span>
                    ) : (
                      <select
                        className="input-base text-[11px] py-0.5 max-w-[260px]"
                        defaultValue=""
                        disabled={!canMutate || rm.patchItem.isPending}
                        onChange={(e) => reassign(item, e.target.value)}
                      >
                        <option value="">— przypisz działkę —</option>
                        {/* wiersze przeglądu są per (działka × właściciel), a pozycja zwrotu
                            przypisuje się do DZIAŁKI — jedna opcja na działkę, bez duplikatów */}
                        {rows.filter((r, i, all) => all.findIndex((x) => x.parcel.id === r.parcel.id) === i).map((r) => (
                          <option key={r.parcel.id} value={r.parcel.id}>
                            {r.parcel.teryt} ({r.owners.map((o) => o.full_name).join(", ") || "—"})
                          </option>
                        ))}
                      </select>
                    )}
                    {item.confidence != null && (
                      <span className="text-[10px] text-[var(--color-text-muted)]">
                        pewność AI: {Math.round(item.confidence * 100)}%
                      </span>
                    )}
                    {item.detected_z7_consent_form && (
                      <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 border border-green-300 text-green-700 dark:text-green-400"
                            title="Forma tytułu wykryta z treści zwrotu (AI). Po zatwierdzeniu przekazania trafi na tytuł prawny właściciela.">
                        tytuł (AI): {Z7_FORM_LABEL[item.detected_z7_consent_form] ?? item.detected_z7_consent_form}
                      </span>
                    )}
                    <span className={`ml-auto font-mono uppercase text-[10px] ${
                      item.status === "accepted" ? "text-green-700"
                        : item.status === "rejected" ? "text-red-600"
                        : item.status === "applied" ? "text-green-700"
                        : "text-[var(--color-text-muted)]"
                    }`}>
                      {item.status === "proposed" ? "propozycja"
                        : item.status === "accepted" ? "zaakceptowany"
                        : item.status === "rejected" ? "odrzucony" : "zastosowany"}
                    </span>
                    <button
                      type="button"
                      onClick={() => openPreview(item)}
                      className="inline-flex items-center gap-1 text-[11px] underline decoration-dotted"
                    >
                      {previewing === item.id ? <Spinner className="h-3 w-3" /> : <Eye size={12} />} podgląd
                    </button>
                  </div>

                  {item.has_annotations && (
                    <div className="text-[11px] space-y-0.5">
                      <p className="flex items-center gap-1 text-amber-700 dark:text-amber-400 font-medium">
                        <AlertTriangle size={12} /> Dopiski/warunki — wymaga przeglądu (akcept zablokowany bez notatki):
                      </p>
                      {item.annotations.map((a, i) => (
                        <p key={i} className="pl-4 text-[var(--color-text-secondary)]">
                          str. {a.page} · <span className="font-mono uppercase text-[10px]">{a.kind}</span>
                          {a.quote && <> · „{a.quote}”</>}
                          {a.note && <> — {a.note}</>}
                        </p>
                      ))}
                    </div>
                  )}
                  {item.review_note && (
                    <p className="text-[11px] text-[var(--color-text-muted)]">
                      Notatka przeglądu ({item.reviewed_by ?? "—"}): {item.review_note}
                    </p>
                  )}

                  {canMutate && item.status !== "applied" && (
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        className="input-base text-[11px] py-0.5 flex-1 min-w-[200px]"
                        placeholder={item.has_annotations
                          ? "Notatka z przeglądu dopisków (wymagana do akceptu)…"
                          : "Notatka (wymagana przy odrzuceniu)…"}
                        value={notes[item.id] ?? ""}
                        onChange={(e) => setNotes((s) => ({ ...s, [item.id]: e.target.value }))}
                      />
                      {item.status !== "accepted" && (
                        <Button
                          size="sm" variant="secondary"
                          disabled={rm.patchItem.isPending || (item.has_annotations && !(notes[item.id] ?? "").trim())}
                          onClick={() => decide(item, "accept")}
                        >
                          <CheckCircle2 size={12} className="mr-1" /> Akceptuj
                        </Button>
                      )}
                      {item.status !== "rejected" && (
                        <Button
                          size="sm" variant="danger"
                          disabled={rm.patchItem.isPending || !(notes[item.id] ?? "").trim()}
                          onClick={() => decide(item, "reject")}
                          title="Odrzucenie loguje powód u negocjatora i ustawia status problem"
                        >
                          <XCircle size={12} className="mr-1" /> Odrzuć
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {items.length === 0 && (
                <p className="text-[11px] text-[var(--color-text-muted)]">
                  Brak pozycji — uruchom najpierw analizę (QR + AI).
                </p>
              )}
            </div>

            <div className="flex items-center gap-3 border-t border-[var(--color-border-muted)] pt-2">
              {canMutate && (
                <Button
                  disabled={accepted === 0 || rm.apply.isPending}
                  loading={rm.apply.isPending}
                  onClick={() =>
                    rm.apply.mutate(batchId, { onError: (e) => setError(extractError(e)) })
                  }
                  title="Wytnij zaakceptowane pakiety i podepnij: wniosek (zwrot terenowy) + przekazania WPW właścicieli"
                >
                  Zastosuj zaakceptowane ({accepted})
                </Button>
              )}
              <span className="text-[11px] text-[var(--color-text-muted)]">
                do decyzji: {pending}
              </span>
              <Button variant="ghost" size="sm" className="ml-auto" onClick={onClose}>
                Zamknij
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function extractError(err: unknown): string {
  const detail = (err as { response?: { data?: { errors?: { detail?: unknown } } } })?.response
    ?.data?.errors?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const first = Object.values(detail as Record<string, unknown>).flat()[0];
    if (typeof first === "string") return first;
  }
  return "Operacja nie powiodła się.";
}
